import ACLStore from "lib/aclStore";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import { ACLStorage } from "./aclStorage";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import {
  CONTROL_PARAMETERS_CONFIGURATION_MAP,
  CONTROL_PARAMETERS_CONFIGURATION_MAP_FOR_PARENT,
  CONTROL_PARAMETERS_CONFIGURATION_MAP_FOR_STUDENT,
  CONTROL_PARAMETERS_STAFF_PERMISSION_MAP,
  getConfigurationsFromControlParameters,
} from "@toddle-edu/common-utils";
import _ from "lodash";
import { getConfigurationBasicDetailsFromCache } from "./modules/GraphqlHelpers";
import {
  parsePreferenceValueObject,
  getProgramTypeFromLocalStorage,
  getProgramTypeFromLocalStorageBasedOnContext,
  getProjectGroupTypeFromRedux,
  getProjectGroupTypeBasedOnContext,
  isUserVisitor,
  getContextIdForConfigurations,
} from "./modules/Utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

const getUserType = state => {
  const userInfo = getUserInfo();
  return userInfo.user_type;
};

class ACLStorageWrapper extends ACLStorage {
  constructor() {
    super();
  }

  #getConfigurationsValueForStaff(controlParameterName) {
    const configurations =
      CONTROL_PARAMETERS_CONFIGURATION_MAP[controlParameterName];

    /**
     * If nothing is found in the configuation map, then it means controlParmeterName isn't present
     * in the old architecture. So, it should be allowed by default.
     */
    if (_.isEmpty(configurations)) {
      return true;
    }

    return ACLStore.can(configurations, "all");
  }

  #getConfigurationsValueForStaffByContextFallback(
    controlParameterName,
    contexts
  ) {
    const contextId = getContextIdForConfigurations(contexts);

    const configurations =
      CONTROL_PARAMETERS_CONFIGURATION_MAP[controlParameterName];

    /**
     * If nothing is found in the configuation map, then it means controlParmeterName does not present
     * in the old architecture. So, it should be allowed by default.
     */
    if (_.isEmpty(configurations)) {
      return true;
    }

    return _.every(configurations, configuration => {
      return (
        parsePreferenceValueObject(
          getConfigurationBasicDetailsFromCache(
            `${contextId}::${configuration}`
          )
        ) === true
      );
    });
  }

  #getConfigurationsValueForStaffByContext(configuration, contexts) {
    const contextId = getContextIdForConfigurations(contexts);

    return (
      parsePreferenceValueObject(
        getConfigurationBasicDetailsFromCache(`${contextId}::${configuration}`)
      ) === true
    );
  }

  /**
   * Q: Why defaultStaffAccess is required?
   * A: In most of the cases, when staffAccessPermission is not defined in the mappings,
   *    it means that staffAccessPermission is not required for that controlParameter and hence
   *    function should return true. Howwever, special cases like AdminPortalHomePage requires mappings
   *    of both staffAccessPermission and configurations. In that case, defaultStaffAccess is used to
   *    return false when staffAccessPermission is not defined in the mappings.
   */
  #getStaffAccessPermissionValueFallback(
    controlParameterName,
    defaultStaffAccess = true
  ) {
    const { moduleName = "", allowedAccess = [] } =
      CONTROL_PARAMETERS_STAFF_PERMISSION_MAP[controlParameterName] ?? {};

    /**
     * Q: Why returning true when permission is nil?
     * A: Payload will be null when action is not controlled by staffAccessPermission.
     *    That means when role based permission are not enabled, feature should work.
     *    Therefore, true is returned in that case
     */
    if (_.isEmpty(allowedAccess)) {
      return defaultStaffAccess;
    }

    const staffPermissionAccess = ACLStore.getUserModuleAccess(moduleName);

    return _.includes(allowedAccess, staffPermissionAccess);
  }

  #getStaffAccessPermissionValue(staffPermissionConfig) {
    if (_.isEmpty(staffPermissionConfig)) return true;

    const { allowedAccess = [], name } = staffPermissionConfig;

    const configObj = ACLStore.getConfigObj(name);

    /**
     * This case is useful when feature is not shown to teacher like user currently.
     * For example, some cards/modules in Admin Portal are not meant for the teacher.
     */
    if (configObj.isEnabled === false) {
      return false;
    }

    const access = parsePreferenceValueObject(configObj);

    return _.includes(allowedAccess, access);
  }

  /**
   * Q: Why defaultStaffAccess is required?
   * A: In most of the cases, when staffAccessPermission is not defined in the mappings,
   *    it means that staffAccessPermission is not required for that controlParameter and hence
   *    function should return true. Howwever, special cases like AdminPortalHomePage requires mappings
   *    of both staffAccessPermission and configurations. In that case, defaultStaffAccess is used to
   *    return false when staffAccessPermission is not defined in the mappings.
   */
  #getStaffAccessPermissionValueByContextFallback(
    controlParameterName,
    contexts,
    defaultStaffAccess = true
  ) {
    const { allowedAccess = [], configuration } =
      CONTROL_PARAMETERS_STAFF_PERMISSION_MAP[controlParameterName] ?? {};

    const contextId = getContextIdForConfigurations(contexts);

    /**
     * Q: Why returning true when permission is nil?
     * A: Payload will be null when action is not controlled by staffAccessPermission.
     *    That means when role based permission are not enabled, feature should work.
     *    Therefore, true is returned in that case
     */
    if (_.isEmpty(allowedAccess)) {
      return defaultStaffAccess;
    }

    const staffPermissionAccess = parsePreferenceValueObject(
      getConfigurationBasicDetailsFromCache(`${contextId}::${configuration}`)
    );

    return _.includes(allowedAccess, staffPermissionAccess);
  }

  #getStaffAccessPermissionValueByContext(staffPermissionConfig, contexts) {
    if (_.isEmpty(staffPermissionConfig)) {
      return true;
    }

    const { allowedAccess = [], name } = staffPermissionConfig;

    const contextId = getContextIdForConfigurations(contexts);

    const staffPermissionAccess = parsePreferenceValueObject(
      getConfigurationBasicDetailsFromCache(`${contextId}::${name}`)
    );

    /**
     * This case is useful when feature is not shown to teacher like user currently.
     * For example, some cards/modules in Admin Portal are not meant for the teacher.
     */
    if (_.isEmpty(allowedAccess)) {
      return false;
    }

    return _.includes(allowedAccess, staffPermissionAccess);
  }

  #getModuleSettingsValueForStudentAndParent(moduleSettingName) {
    const userType = getUserType();

    const configurations =
      userType == "student"
        ? CONTROL_PARAMETERS_CONFIGURATION_MAP_FOR_STUDENT[moduleSettingName]
        : CONTROL_PARAMETERS_CONFIGURATION_MAP_FOR_PARENT[moduleSettingName];

    /**
     * If nothing is found in the configuration map,
     * then it means module is not dependent on moduleSettingName in old architecture.
     * So, it should be allowed by default.
     */

    if (_.isEmpty(configurations)) {
      return true;
    }

    return ACLStore.can(configurations, "all");
  }

  #getPermissionValueForStudentAndParent(moduleSettingName) {
    const userType = getUserType();

    const configurations =
      userType == "student"
        ? CONTROL_PARAMETERS_CONFIGURATION_MAP_FOR_STUDENT[moduleSettingName]
        : CONTROL_PARAMETERS_CONFIGURATION_MAP_FOR_PARENT[moduleSettingName];

    /**
     * If nothing is found in the configuration map,
     * then it means module is not dependent on moduleSettingName in old architecture.
     * So, it should be allowed by default.
     */

    if (_.isEmpty(configurations)) {
      return true;
    }

    return ACLStore.can(configurations, "all");
  }

  #getPermissionValueFallback(permissionName, defaultStaffAccess = true) {
    if (_.isEmpty(permissionName)) {
      return false;
    }

    const userType = getUserType();

    // For student and parent, we check the module settings value based on old architecture
    if (userType != "staff") {
      return this.#getPermissionValueForStudentAndParent(permissionName);
    }

    if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
      return super.getPermissionValue(permissionName);
    }

    const isAdmin = getIsAdminByRole();

    return isAdmin
      ? this.#getConfigurationsValueForStaff(permissionName)
      : //For teachers, we need to consider both regular configurations and staffAccessPermission
        this.#getConfigurationsValueForStaff(permissionName) &&
          this.#getStaffAccessPermissionValueFallback(
            permissionName,
            defaultStaffAccess
          );
  }

  getPermissionValue(permissionName, { userType = getUserType() } = {}) {
    if (_.isEmpty(permissionName)) {
      return false;
    }

    if (
      ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION) &&
      userType === "staff" //For non staff users, we need to check the old architecture
    ) {
      return super.getPermissionValue(permissionName);
    }

    const curriculumProgramType = getProgramTypeFromLocalStorage();

    const projectGroupType = getProjectGroupTypeFromRedux();

    try {
      const {
        configuration,
        defaultValue,
        staffPermissionConfig,
      } = getConfigurationsFromControlParameters({
        controlParameter: permissionName,
        curriculumProgramType,
        userType,
        projectGroupType,
      });

      const isActionAllowed = !_.isNil(configuration)
        ? ACLStore.can(configuration)
        : isUserVisitor()
        ? false //Hack for visitors in Projects
        : defaultValue;

      if (userType !== "staff" || isUserVisitor()) {
        return isActionAllowed;
      }

      const isAdmin = getIsAdminByRole();

      return isAdmin
        ? isActionAllowed
        : isActionAllowed &&
            this.#getStaffAccessPermissionValue(staffPermissionConfig);
    } catch (e) {
      console.error(e);
      /**
       * TEMPORARY: This is required to avoid UI breaking till all mappings are not updated
       * in common utils. This will be removed once all mappings are updated.
       */
      return this.#getPermissionValueFallback(permissionName);
    }
  }

  #getModuleSettingsValueFallback(moduleSettingName, defaultAccess = true) {
    if (_.isEmpty(moduleSettingName)) {
      return false;
    }

    const userType = getUserType();

    // For student and parent, we check the module settings value based on old architecture
    if (userType != "staff") {
      return this.#getModuleSettingsValueForStudentAndParent(moduleSettingName);
    }

    if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
      return super.getModuleSettingsValue(moduleSettingName);
    }

    const isAdmin = getIsAdminByRole();

    return isAdmin
      ? this.#getConfigurationsValueForStaff(moduleSettingName)
      : //For teachers, we need to consider both regular configurations and staffAccessPermission
        this.#getConfigurationsValueForStaff(moduleSettingName) &&
          this.#getStaffAccessPermissionValueFallback(
            moduleSettingName,
            defaultAccess
          );
  }

  getModuleSettingsValue(moduleSettingName, { userType = getUserType() } = {}) {
    if (_.isEmpty(moduleSettingName)) {
      return false;
    }

    if (
      ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION) &&
      userType === "staff" //For non staff users, we need to check the old architecture
    ) {
      return super.getModuleSettingsValue(moduleSettingName);
    }

    const curriculumProgramType = getProgramTypeFromLocalStorage();

    const projectGroupType = getProjectGroupTypeFromRedux();

    try {
      const {
        configuration,
        defaultValue,
        staffPermissionConfig,
      } = getConfigurationsFromControlParameters({
        controlParameter: moduleSettingName,
        curriculumProgramType,
        userType,
        projectGroupType,
      });

      const isModuleEnabled = !_.isNil(configuration)
        ? ACLStore.can(configuration)
        : isUserVisitor()
        ? false //Hack for visitors in Projects
        : defaultValue;

      if (userType !== "staff" || isUserVisitor()) {
        return isModuleEnabled;
      }

      const isAdmin = getIsAdminByRole();

      return isAdmin
        ? isModuleEnabled
        : isModuleEnabled &&
            this.#getStaffAccessPermissionValue(staffPermissionConfig);
    } catch (e) {
      console.error(e);
      /**
       * TEMPORARY: This is required to avoid UI breaking till all mappings are not updated
       * in common utils. This will be removed once all mappings are updated.
       */
      return this.#getModuleSettingsValueFallback(moduleSettingName);
    }
  }

  #getPermissionValueByContextFallback(
    permissionName,
    contexts,
    defaultStaffAccess = true
  ) {
    if (_.isEmpty(permissionName)) {
      return false;
    }

    if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
      return super.getPermissionValueByContext(permissionName, contexts);
    }

    const isAdmin = getIsAdminByRole();

    return isAdmin
      ? this.#getConfigurationsValueForStaffByContextFallback(
          permissionName,
          contexts
        )
      : this.#getConfigurationsValueForStaffByContextFallback(
          permissionName,
          contexts
        ) &&
          this.#getStaffAccessPermissionValueByContextFallback(
            permissionName,
            contexts,
            defaultStaffAccess
          );
  }

  getPermissionValueByContext(
    permissionName,
    contexts,
    { userType = getUserType() } = {}
  ) {
    if (_.isEmpty(permissionName)) {
      return false;
    }

    if (
      ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION) &&
      userType === "staff" //For non staff users, we need to check the old architecture
    ) {
      return super.getPermissionValueByContext(permissionName, contexts);
    }

    const curriculumProgramType = getProgramTypeFromLocalStorageBasedOnContext(
      contexts
    );

    const projectGroupType = getProjectGroupTypeBasedOnContext(contexts);

    try {
      const {
        configuration,
        defaultValue,
        staffPermissionConfig,
      } = getConfigurationsFromControlParameters({
        controlParameter: permissionName,
        curriculumProgramType,
        userType,
        projectGroupType,
      });

      const isActionAllowed = !_.isNil(configuration)
        ? this.#getConfigurationsValueForStaffByContext(configuration, contexts)
        : isUserVisitor()
        ? false //Hack for visitors in Projects
        : defaultValue;

      if (userType !== "staff" || isUserVisitor()) {
        return isActionAllowed;
      }

      const isAdmin = getIsAdminByRole();

      return isAdmin
        ? isActionAllowed
        : isActionAllowed &&
            this.#getStaffAccessPermissionValueByContext(
              staffPermissionConfig,
              contexts
            );
    } catch (e) {
      console.error(e);
      /**
       * TEMPORARY: This is required to avoid UI breaking till all mappings are not updated
       * in common utils. This will be removed once all mappings are updated.
       */
      return this.#getPermissionValueByContextFallback(
        permissionName,
        contexts
      );
    }
  }

  #getModuleSettingsValueByContextFallback(
    moduleSettingName,
    contexts,
    defaultStaffAccess = true
  ) {
    if (_.isEmpty(moduleSettingName)) {
      return false;
    }

    if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
      return super.getModuleSettingsValueByContext(moduleSettingName, contexts);
    }

    /**
     * Need to think about handling these check in better way as we might encounter user type based checks later.
     */
    const isAdmin = getIsAdminByRole();

    return isAdmin
      ? this.#getConfigurationsValueForStaffByContextFallback(
          moduleSettingName,
          contexts
        )
      : this.#getConfigurationsValueForStaffByContextFallback(
          moduleSettingName,
          contexts
        ) &&
          this.#getStaffAccessPermissionValueByContextFallback(
            moduleSettingName,
            contexts,
            defaultStaffAccess
          );
  }

  getModuleSettingsValueByContext(
    moduleSettingName,
    contexts,
    { userType = getUserType(), addOnContexts } = {}
  ) {
    if (_.isEmpty(moduleSettingName)) {
      return false;
    }

    if (
      ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION) &&
      userType === "staff" //For non staff users, we need to check the old architecture
    ) {
      return super.getModuleSettingsValueByContext(
        moduleSettingName,
        contexts,
        addOnContexts
      );
    }

    const curriculumProgramType = getProgramTypeFromLocalStorageBasedOnContext(
      contexts
    );

    const projectGroupType = getProjectGroupTypeBasedOnContext(contexts);

    try {
      const {
        configuration,
        defaultValue,
        staffPermissionConfig,
      } = getConfigurationsFromControlParameters({
        controlParameter: moduleSettingName,
        curriculumProgramType,
        userType,
        projectGroupType,
      });

      const isModuleEnabled = !_.isNil(configuration)
        ? this.#getConfigurationsValueForStaffByContext(configuration, contexts)
        : isUserVisitor()
        ? false //Hack for visitors in Projects
        : defaultValue;

      if (userType !== "staff" || isUserVisitor()) {
        return isModuleEnabled;
      }

      const isAdmin = getIsAdminByRole();

      return isAdmin
        ? isModuleEnabled
        : isModuleEnabled &&
            this.#getStaffAccessPermissionValueByContext(
              staffPermissionConfig,
              contexts
            );
    } catch (e) {
      console.error(e);
      /**
       * TEMPORARY: This is required to avoid UI breaking till all mappings are not updated
       * in common utils. This will be removed once all mappings are updated.
       */

      return this.#getModuleSettingsValueByContextFallback(
        moduleSettingName,
        contexts
      );
    }
  }

  getControlParameterIdByContext(
    controlParameterName,
    controlParameterType,
    contexts
  ) {
    //No backward compatibility required for this function
    return super.getControlParameterIdByContext(
      controlParameterName,
      controlParameterType,
      contexts
    );
  }
}

export default new ACLStorageWrapper();
